import {
   Component,
   EventEmitter,
   Input,
   input,
   OnChanges,
   OnInit,
   Output,
   SimpleChanges,
   ViewChild,
   OnDestroy
} from '@angular/core';
import { Task } from '../../domain/task';
import { UploadFile } from '../../domain/uploadfile';
import { WorkflowFile } from '../../domain/workflow-file';
import NutrientViewer, { ToolbarItem } from '@nutrient-sdk/viewer';
import { Annotation } from '../../domain/annotation';
import { TaskService } from '../../services/task.service';
import { UploadWorkflowFileComponent } from '../upload-workflow-file/upload-workflow-file.component';
import { environment } from '../../../environments/environment';
import * as jose from 'jose';
import DataUtils from '../../util/data-utils';
import { WorkflowType } from '../../domain/enums/workflow-type';
import { UserPermissions } from '../../domain/security/user-permissions';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { firstValueFrom, Subscription } from 'rxjs';
import { PhobosTaskInstance } from '../../domain/phobos-task-instance';
import { ActivatedRoute, Router } from '@angular/router';
import { LocationStoreService } from '../../services/stores/location-store.service';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';
import { Location } from '@angular/common';
import { ModelSummary } from '../../domain/Ai/model-summary';

@Component({
   selector: 'app-pdf-file-panel',
   templateUrl: './pdf-file-panel.component.html',
   styleUrls: ['./pdf-file-panel.component.scss']
})
export class PdfFilePanelComponent implements OnChanges, OnInit, OnDestroy {
   @ViewChild('fileUploadForm')
   uploadForm: UploadWorkflowFileComponent | undefined;

   @Input() hasFile: boolean;
   @Input() isLoading: boolean;
   @Input() isPrimary: boolean;
   @Input() currentTask: Task;
   @Input() containerId: string;
   @Input() currentFile: any;
   @Input() showPanelPadding: string;
   @Input() pdfPanelInFocus: boolean;
   @Input() flexCss: string;
   @Input() phobosTaskInstance: PhobosTaskInstance;
   @Input() aiData?: ModelSummary;

   @Output() goToNextTaskClicked = new EventEmitter();
   @Output() goToPreviousClicked = new EventEmitter();
   @Output() reviewClicked = new EventEmitter();
   @Output() uploadFile = new EventEmitter();
   @Output() groupChange = new EventEmitter();
   @Output() assignWorkflow = new EventEmitter();
   @Output() completeWorkflow = new EventEmitter();
   @Output() reOpenWorkflow = new EventEmitter();
   @Output() resendWorkflowCompletion = new EventEmitter();

   uploadedFiles: UploadFile[] = [];
   userPermissions: UserPermissions;
   private permsReady: Promise<UserPermissions>;
   private lastLoadedDocumentId?: WorkflowFile['documentId'];
   private subs = new Subscription();
   private lastLoadedKey?: string;
   taskListParams: any;
   isSubmitButtonDisabled = input(false);
   previousUrl: string | null = null;
   protected readonly WorkflowType = WorkflowType;

   constructor(
      private taskService: TaskService,
      private permissionsStore: PermissionsStoreService,
      private router: Router,
      private locationStore: LocationStoreService,
      private formTaskService: PhobosTaskInstanceService,
      private _location: Location,
      private route: ActivatedRoute
   ) {
      this.isPrimary = false;
      this.currentTask = new Task();
      this.currentFile = new WorkflowFile();
      this.hasFile = true;
      this.isLoading = false;
      this.containerId = '';
      this.userPermissions = new UserPermissions();
      this.showPanelPadding = '';
      this.pdfPanelInFocus = true;
      this.flexCss = '';
      this.phobosTaskInstance = new PhobosTaskInstance();

      this.permsReady = firstValueFrom(this.permissionsStore.get())
         .then((p) => {
            this.userPermissions = p ?? new UserPermissions();
            return this.userPermissions;
         })
         .catch(() => {
            this.userPermissions = new UserPermissions();
            return this.userPermissions;
         });
   }

   async ngOnInit(): Promise<void> {
      await this.permsReady;

      this.subs.add(
         this.route.queryParams.subscribe((params) => {
            if (this.previousUrl === null && params['from']) {
               this.previousUrl = params['from'];
            }
         })
      );

      const storedParams = localStorage.getItem('tasklist_page');

      if (storedParams != null) {
         try {
            this.taskListParams = JSON.parse(storedParams);
         } catch (error) {
            console.error(error);
         }
      }

      console.log('previousUrl', this.previousUrl);
   }

   ngOnDestroy(): void {
      this.subs.unsubscribe();
      NutrientViewer.unload(`#${this.containerId}`);
   }

   // Method to navigate back.
   backButton(): void {
      if (this.previousUrl) {
         this.router.navigateByUrl(this.previousUrl);
      } else {
         this.router.navigate(['/dashboard']); // Fallback if page is refreshed
      }
   }

   uploadWorkflowFile() {
      this.uploadFile.emit({ event: null, file: this.uploadedFiles });
   }

   saveUserZoomPreference(zoomLevel: number) {
      localStorage.setItem('userZoomPreference', zoomLevel.toString());
   }

   getUserZoomPreference(): number {
      const storedZoom = localStorage.getItem('userZoomPreference');
      // Convert to number, fallback to default (-2)
      return storedZoom ? parseFloat(storedZoom) : -2;
   }

   processFileForUi(file: WorkflowFile) {
      if (file.documentId) {
         this.currentFile = file;
         const filteredToolBarItems = [
            'zoom-out',
            'zoom-in',
            'zoom-mode',
            'export-pdf',
            'pager',
            'pan'
         ];
         if (file.isPrimary) {
            filteredToolBarItems.push(
               ...['rectangle', 'arrow', 'note', 'highlighter', 'ink-eraser']
            );
         } else {
            filteredToolBarItems.push('custom');
         }
         const downloadButton: ToolbarItem = {
            type: 'custom',
            id: 'download-source',
            title: 'Download',
            onPress: () => {
               if (file.data) {
                  DataUtils.downloadBase64File(file);
               } else if (file.documentId && file.id) {
                  this.formTaskService
                     .getSupportingFileSourceByPhobosTaskInstance(
                        file.documentId,
                        file.id
                     )
                     .subscribe((f) => DataUtils.downloadBase64File(f));
               }
            }
         };
         const customButtons: ToolbarItem[] = [downloadButton];
         NutrientViewer.unload(`#${this.containerId}`);
         if (file.storageId) {
            this.loadFileFromServer(file, filteredToolBarItems, customButtons);
         } else {
            this.loadFileFromBase64String(file, filteredToolBarItems, customButtons);
         }
      }
   }

   loadFileFromBase64String(
      file: WorkflowFile,
      filteredToolBarItems: Array<string>,
      customButtons: ToolbarItem[]
   ) {
      const annotationData = [];
      if (file.isPrimary) {
         for (const annotation of file.annotations) {
            annotationData.push(JSON.parse(annotation.instantJson));
         }
      }

      NutrientViewer.load({
         serverUrl: environment.PSPDFKit.serverUrl,
         baseUrl: location.protocol + '//' + location.host + '/assets/',
         document: `data:${this.currentFile.mimeType};base64,${this.currentFile.data}`,
         container: `#${this.containerId}`,
         licenseKey: environment.PSPDFKit.key,
         instantJSON: {
            annotations: annotationData,
            format: 'https://pspdfkit.com/instant-json/v1'
         },
         initialViewState: new NutrientViewer.ViewState({
            zoom: this.getUserZoomPreference()
         })
      }).then((instance) => {
         const items: ToolbarItem[] = instance.toolbarItems.concat(customButtons);
         const annotationPresets = NutrientViewer.defaultAnnotationPresets;

         annotationPresets.rectangle = {
            blendMode: 'normal',
            opacity: 100,
            color: NutrientViewer.Color.BLACK,
            strokeColor: NutrientViewer.Color.BLACK,
            lineWidth: 10
         };

         annotationPresets.arrow = {
            blendMode: 'normal',
            opacity: 100,
            color: NutrientViewer.Color.BLACK,
            strokeColor: NutrientViewer.Color.BLACK,
            lineWidth: 10
         };

         annotationPresets.highlighter = {
            blendMode: 'normal',
            color: NutrientViewer.Color.YELLOW,
            strokeColor: NutrientViewer.Color.YELLOW,
            opacity: 0.3,
            lineWidth: 10
         };

         instance.addEventListener('viewState.change', (newState) => {
            const zoomLevel = typeof newState.zoom === 'number' ? newState.zoom : -2;
            this.saveUserZoomPreference(zoomLevel);
         });

         instance.setAnnotationPresets(annotationPresets);
         instance.setToolbarItems(
            items.filter((item: { type: string }) =>
               filteredToolBarItems.includes(item.type)
            )
         );
         if (this.userPermissions.forms.create) {
            instance.setToolbarItems((items) => [
               ...items,
               { type: 'form-creator' } as unknown as ToolbarItem
            ]);
         }
         instance.addEventListener('annotations.update', (createdAnnotations) => {
            this.processAnnotations(createdAnnotations);
         });
         instance.addEventListener('annotations.create', (createdAnnotations) => {
            this.processAnnotations(createdAnnotations);
         });
      });
   }

   async loadFileFromServer(
      file: WorkflowFile,
      filteredToolBarItems: Array<string>,
      customButtons: ToolbarItem[]
   ) {
      if (file.storageId) {
         let readOnly = false;
         if (this.phobosTaskInstance.completedDate) {
            readOnly = true;
         } else {
            readOnly = false;
         }

         const token = await this.prepareJwt(file.storageId);
         NutrientViewer.load({
            serverUrl: environment.PSPDFKit.serverUrl,
            baseUrl: location.protocol + '//' + location.host + '/assets/',
            container: `#${this.containerId}`,
            licenseKey: environment.PSPDFKit.key,
            documentId: file.storageId,
            authPayload: { jwt: token },
            initialViewState: new NutrientViewer.ViewState({
               zoom: this.getUserZoomPreference(),
               readOnly: readOnly
            }),
            instant: true,
            autoSaveMode: NutrientViewer.AutoSaveMode.IMMEDIATE
         })
            .then((instance) => {
               const items: ToolbarItem[] =
                  instance.toolbarItems.concat(customButtons);
               const annotationPresets = NutrientViewer.defaultAnnotationPresets;
               annotationPresets.rectangle = {
                  blendMode: 'normal',
                  opacity: 100,
                  color: NutrientViewer.Color.BLACK,
                  strokeColor: NutrientViewer.Color.BLACK,
                  lineWidth: 10
               };
               annotationPresets.arrow = {
                  blendMode: 'normal',
                  opacity: 100,
                  color: NutrientViewer.Color.BLACK,
                  strokeColor: NutrientViewer.Color.BLACK,
                  lineWidth: 10
               };
               annotationPresets.highlighter = {
                  blendMode: 'normal',
                  color: NutrientViewer.Color.YELLOW,
                  strokeColor: NutrientViewer.Color.YELLOW,
                  opacity: 0.3,
                  lineWidth: 10
               };

               instance.addEventListener('viewState.change', (newState) => {
                  const zoomLevel =
                     typeof newState.zoom === 'number' ? newState.zoom : -2; // Default zoom if it's not a number
                  this.saveUserZoomPreference(zoomLevel); // Save only valid number zoom levels
               });

               instance.setAnnotationPresets(annotationPresets);
               instance.setToolbarItems(
                  items.filter((item: { type: string }) =>
                     filteredToolBarItems.includes(item.type)
                  )
               );
               if (this.userPermissions.forms.create) {
                  instance.setToolbarItems((items) => [
                     ...items,
                     { type: 'form-creator' } as unknown as ToolbarItem
                  ]);
               }
               console.log('PSPDFKit server loaded');
            })
            .catch(function () {
               console.error();
            });
      }
   }

   async prepareJwt(documentId: string) {
      const jwtKey = environment.PSPDFKit.serverPem;
      const claims = {
         document_id: documentId,
         permissions: ['read-document', 'write', 'download']
      };

      const privateKey = await jose.importPKCS8(jwtKey, 'RS256');
      return await new jose.SignJWT(claims)
         .setProtectedHeader({ alg: 'RS256' })
         .setExpirationTime('3d')
         .sign(privateKey);
   }

   processAnnotations(createdAnnotations: any): void {
      for (const pdfAnnotation of createdAnnotations) {
         const annotation = new Annotation();
         if (this.currentFile.documentId) {
            annotation.documentId = this.currentFile.documentId;
         }
         annotation.instantJson = JSON.stringify(
            NutrientViewer.Annotations.toSerializableObject(pdfAnnotation)
         );
         annotation.annotationId = pdfAnnotation.id;

         this.taskService
            .addAnnotation(annotation, this.currentTask.processId)
            .subscribe(() => {});
      }
   }

   onReview() {
      this.reviewClicked.emit();
   }

   goToNextTask() {
      this.uploadedFiles = [];
      this.uploadForm?.clearUploadForm();
      this.goToNextTaskClicked.emit();
   }

   goToPreviousTask() {
      this.uploadedFiles = [];
      this.uploadForm?.clearUploadForm();
      this.goToPreviousClicked.emit();
   }

   isReviewableForm() {
      if (
         this.currentFile != null &&
         this.currentFile.documentId != undefined &&
         this.currentTask.isAssignedToUser &&
         this.currentTask.type == WorkflowType.Form
      ) {
         return (
            this.currentTask.isComplete === false ||
            !this.phobosTaskInstance.completedDate
         );
      }
      return false;
   }

   isReviewable() {
      if (
         this.currentFile != null &&
         this.currentFile.documentId != undefined &&
         this.currentTask.isAssignedToUser &&
         this.currentTask.type != WorkflowType.Form
      ) {
         return !this.currentTask.isComplete;
      }
      return false;
   }

   ngOnChanges(changes: SimpleChanges): void {
      const nextFile = changes.currentFile?.currentValue as WorkflowFile | undefined;
      if (!nextFile) return;

      const key = `${nextFile.documentId ?? ''}|${nextFile.storageId ?? ''}|${nextFile.id ?? ''}`;
      if (!nextFile.documentId || key === this.lastLoadedKey) return;

      this.lastLoadedKey = key;

      void this.permsReady.then(() => this.processFileForUi(nextFile));
   }

   onAssign($event: any) {
      $event.preventDefault();
      this.assignWorkflow.emit($event);
   }

   onComplete($event: any) {
      $event.preventDefault();
      this.completeWorkflow.emit($event);
   }

   onReopen($event: any) {
      $event.preventDefault();
      this.reOpenWorkflow.emit($event);
   }

   relayGroupChange($event: any) {
      this.groupChange.emit($event);
   }

   onResend($event: any) {
      $event.preventDefault();
      this.resendWorkflowCompletion.emit($event);
   }
}
